import React, { useRef, useEffect, useState } from 'react';

interface AutoExpandTextareaProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  maxLength?: number;
  className?: string;
  minRows?: number;
  maxRows?: number;
  focusRows?: number;
}

export const AutoExpandTextarea: React.FC<AutoExpandTextareaProps> = ({
  value,
  onChange,
  onKeyDown,
  placeholder = '添加描述（可选）...',
  maxLength = 20000,
  className = '',
  minRows = 2,
  maxRows = 30,
  focusRows = 4,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [currentRows, setCurrentRows] = useState(minRows);

  // 计算内容需要的行数
  const calculateRows = (text: string): number => {
    if (!textareaRef.current) return minRows;

    const textarea = textareaRef.current;
    const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight);
    const paddingTop = parseInt(window.getComputedStyle(textarea).paddingTop);
    const paddingBottom = parseInt(window.getComputedStyle(textarea).paddingBottom);

    // 临时设置为单行来获取滚动高度
    textarea.style.height = 'auto';
    const scrollHeight = textarea.scrollHeight - paddingTop - paddingBottom;
    const calculatedRows = Math.ceil(scrollHeight / lineHeight);

    return calculatedRows;
  };

  // 自适应调整高度
  useEffect(() => {
    if (!textareaRef.current) return;

    const contentRows = calculateRows(value);

    if (isFocused) {
      // focus状态：至少显示focusRows行，最多maxRows行
      setCurrentRows(Math.max(focusRows, Math.min(contentRows, maxRows)));
    } else {
      // 非focus状态：根据内容显示，最少minRows，最多maxRows
      setCurrentRows(Math.max(minRows, Math.min(contentRows, maxRows)));
    }
  }, [value, isFocused, minRows, maxRows, focusRows]);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= maxLength) {
      onChange(newValue);
    }
  };

  return (
    <div className="relative rounded-md border border-gray-200 bg-white px-3 py-2 transition-colors focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        placeholder={placeholder}
        rows={currentRows}
        className={`w-full text-xs border-0 bg-transparent placeholder-gray-400 focus:outline-none resize-none overflow-y-auto ${className}`}
        style={{
          minHeight: `${minRows * 1.5}rem`,
          maxHeight: `${maxRows * 1.5}rem`,
        }}
      />
      {maxLength && value.length > maxLength * 0.8 && (
        <div className="flex justify-end mt-1">
          <span className={`text-xs ${
            value.length > maxLength * 0.9 ? 'text-orange-500' : 'text-gray-400'
          }`}>
            {value.length}/{maxLength}
          </span>
        </div>
      )}
    </div>
  );
};
